import { createContext, useContext, useEffect, useRef } from 'react'
import { sendEngagement } from './usage'

/** The page view a rendered report belongs to. Provided by the report page, read by anything that counts. */
export interface ReportVisit {
  viewId?: string
  loadMilliseconds?: number
}

export const ReportVisitContext = createContext<ReportVisit>({})

/** The current report's page view. Empty outside a loaded report. */
export function useReportVisit(): ReportVisit {
  return useContext(ReportVisitContext)
}

/**
 * Past this, nothing more is sent for the view.
 *
 * The usage database is serverless and billed by the hour it is awake, and every write keeps it
 * awake for another hour. A report is read in minutes; a tab reopened the next morning would wake it
 * for a single row. Half an hour keeps every event close to the lookup it belongs to.
 */
const REPORTING_WINDOW_MS = 30 * 60 * 1000

/** A ceiling on events per view, for a reader who flips between tabs all afternoon. */
const MAX_SENDS = 10

/** Matches the API's own ceiling; longer is a tab left open, not reading. */
const MAX_VISIBLE_SECONDS = 3600

/**
 * Reports how this report is read: seconds actually visible, and which sections came into view.
 *
 * Sent when the page is hidden or left — switching tabs, closing it, navigating to another plate —
 * and again on the next hide if anything changed. Each send carries running totals, so the API keeps
 * every row and a query takes the largest per view.
 *
 * "Reached" means a section's top came a quarter of the way up the screen, not merely that one
 * pixel of it showed. The schade card being in that list is what makes its click-through rate
 * meaningful: most readers never scroll to most cards, and dividing clicks by every lookup would
 * understate the link badly.
 *
 * @param sectionIds The anchor ids of the rendered sections, in page order. Changes as the late
 *   arrivals (rarity, defect profile) render, and the observer follows.
 */
export function useReportEngagement(sectionIds: readonly string[]): void {
  const { viewId, loadMilliseconds } = useReportVisit()
  const reached = useRef(new Set<string>())
  const order = useRef<readonly string[]>(sectionIds)
  const observed = sectionIds.join(',')

  useEffect(() => {
    const ids = observed ? observed.split(',') : []
    order.current = ids

    if (typeof IntersectionObserver === 'undefined') {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            reached.current.add(entry.target.id)
          }
        }
      },
      { rootMargin: '0px 0px -25% 0px' },
    )

    for (const id of ids) {
      const element = document.getElementById(id)
      if (element) {
        observer.observe(element)
      }
    }

    return () => observer.disconnect()
  }, [observed])

  useEffect(() => {
    if (!viewId) {
      return
    }

    const mountedAt = performance.now()
    let visibleMs = 0
    let visibleSince = document.visibilityState === 'visible' ? mountedAt : undefined
    let sends = 0
    let lastSent = ''

    const send = () => {
      const now = performance.now()

      if (sends >= MAX_SENDS || now - mountedAt > REPORTING_WINDOW_MS) {
        return
      }

      const totalMs = visibleMs + (visibleSince === undefined ? 0 : now - visibleSince)
      const visibleSeconds = Math.min(MAX_VISIBLE_SECONDS, Math.round(totalMs / 1000))
      const reachedSections = order.current.filter((id) => reached.current.has(id))

      // Nothing new since the last send: a second row would only cost a database write.
      const fingerprint = `${visibleSeconds}|${reachedSections.join(',')}`
      if (fingerprint === lastSent) {
        return
      }

      lastSent = fingerprint
      sends += 1
      sendEngagement({ viewId, visibleSeconds, loadMilliseconds, reachedSections })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (visibleSince !== undefined) {
          visibleMs += performance.now() - visibleSince
          visibleSince = undefined
        }
        send()
      } else if (visibleSince === undefined) {
        visibleSince = performance.now()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', send)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', send)
      // Leaving for another plate inside the app unmounts the report without hiding the page.
      send()
    }
  }, [viewId, loadMilliseconds])
}
